import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, Star, Shield, ArrowLeft, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PaymentService, UserPackageService } from '@/integrations/supabase/services';
import { usePayment } from '@/hooks/usePayment';
import { useNavigate } from 'react-router-dom';
import { useCompletionStatus } from '@/hooks/useCompletionStatus';
import { DashboardLayout } from '@/components/DashboardLayout';
import '@/styles/animations.css';

interface UserPackage {
  id: string;
  user_id: string;
  package_type: string;
  status: string;
  created_at: string;
  updated_at: string;
}

const YourPackage = () => {
  const { user } = useAuth();
  const { createPayment, selectFreePackage, loading } = usePayment();
  const [currentPackage, setCurrentPackage] = useState<string>('free');
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null);
  const [loadingPackage, setLoadingPackage] = useState(true);
  const navigate = useNavigate();
  const { completionStatus } = useCompletionStatus();

  useEffect(() => {
    if (user) {
      loadUserPackage();
    }
  }, [user]);

  const loadUserPackage = async () => {
    if (!user) return;
    
    try {
      // First check user_packages table for current package
      const userPackage = await UserPackageService.getByUserId(user.uid);
      
      if (userPackage) {
        setCurrentPackage(userPackage.package_type || 'free');
      } else {
        // Fallback to payments table for backward compatibility
        const payments = await PaymentService.getByUserId(user.uid);
        const packageData = payments.find(p => p.status === 'completed');

        if (packageData) {
          setCurrentPackage(packageData.package_type || 'free');
          // Create user package record for future use
          await UserPackageService.upsert(user.uid, packageData.package_type, 'active');
        } else {
          setCurrentPackage('free');
          // Create default free package record
          await UserPackageService.upsert(user.uid, 'free', 'active');
        }
      }
    } catch (error) {
      console.error('Error loading user package:', error);
      setCurrentPackage('free');
    } finally {
      setLoadingPackage(false);
    }
  };

  const packages = [
    {
      id: 'free',
      name: 'Free Consultation',
      price: 'Free',
      description: 'Basic guidance to get you started',
      icon: <Star className="h-8 w-8" />,
      features: [
        'Initial consultation call',
        'Basic document checklist',
        'General guidance',
        'Email support'
      ],
      color: 'bg-primary/10',
      iconColor: 'text-primary'
    },
    {
      id: 'basic',
      name: 'Basic Package',
      price: '1299 PLN',
      description: 'Essential support for your application',
      icon: <Check className="h-8 w-8" />,
      features: [
        'Document review and feedback',
        'Application form assistance',
        'Email and phone support',
        'Basic appointment scheduling help'
      ],
      color: 'bg-primary/10',
      iconColor: 'text-primary'
    },
    {
      id: 'standard',
      name: 'Standard Package',
      price: '2199 PLN',
      description: 'Comprehensive application support',
      icon: <Shield className="h-8 w-8" />,
      features: [
        'Full application review',
        'Document preparation assistance',
        'Priority support',
        'Appointment booking assistance',
        'Interview preparation'
      ],
      color: 'bg-primary/10',
      iconColor: 'text-primary'
    },
    {
      id: 'premium',
      name: 'Premium Package',
      price: '3499 PLN',
      description: 'Complete white-glove service',
      icon: <Crown className="h-8 w-8" />,
      features: [
        'End-to-end application management',
        'Personal immigration consultant',
        '24/7 priority support',
        'Document translation service',
        'Follow-up and status tracking',
        'Success guarantee'
      ],
      color: 'bg-primary/10',
      iconColor: 'text-primary'
    }
  ];

  const handlePackageSelect = (packageId: string) => {
    setSelectedPackage(packageId);
  };

  const handleUpgrade = async () => {
    if (!selectedPackage) return;
    
    if (selectedPackage === 'free') {
      // Handle free package selection
      const result = await selectFreePackage();
      if (result?.success) {
        setCurrentPackage('free');
        setSelectedPackage(null);
        // Refresh the page to show updated package
        await loadUserPackage();
      }
    } else {
      // Handle paid package selection
      const result = await createPayment(selectedPackage);
      if (result?.success) {
        setCurrentPackage(selectedPackage);
        setSelectedPackage(null);
        // Refresh the page to show updated package
        await loadUserPackage();
      }
    }
  };

  const handleBack = () => {
    setSelectedPackage(null);
  };

  const handleContinue = () => {
    navigate('/personal-info');
  };

  if (loadingPackage) {
    return (
      <DashboardLayout pageTitle="Your Package">
        <div className="text-center">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle="Your Package">
      <div className="space-y-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4 font-alumni">Your Support Package</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
            Choose the level of support that's right for your Polish residence card application
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {packages.map((pkg) => {
            const isCurrentPackage = pkg.id === currentPackage;
            const isSelected = pkg.id === selectedPackage;
            const canUpgrade = currentPackage === 'free' || 
              (currentPackage === 'basic' && ['standard', 'premium'].includes(pkg.id)) ||
              (currentPackage === 'standard' && pkg.id === 'premium');

            return (
              <Card 
                key={pkg.id} 
                className={`relative transition-all duration-300 hover:shadow-lg backdrop-blur-sm group cursor-pointer
                  ${isCurrentPackage ? 'ring-2 ring-primary' : ''}
                  ${isSelected ? 'ring-2 ring-primary scale-105' : ''}
                  ${!canUpgrade && !isCurrentPackage ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                onClick={() => canUpgrade && handlePackageSelect(pkg.id)}
              >
                {isCurrentPackage && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground px-4 py-1">
                      Current Package
                    </Badge>
                  </div>
                )}

                <CardHeader className="text-center">
                  <div className={`mx-auto p-3 rounded-full ${pkg.color} mb-4 group-hover:bg-primary/20 transition-colors animate-float`}>
                    <div className={pkg.iconColor}>
                      {pkg.icon}
                    </div>
                  </div>
                  <CardTitle className="text-xl font-alumni">{pkg.name}</CardTitle>
                  <CardDescription className="font-hind">{pkg.description}</CardDescription>
                  <div className="text-3xl font-bold text-primary mt-2">{pkg.price}</div>
                </CardHeader>

                <CardContent>
                  <ul className="space-y-2">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2 text-muted-foreground group-hover:text-foreground/80 transition-colors">
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                        <span className="text-sm font-hind">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row sm:justify-between items-center mt-8 pt-6 border-t border-border gap-4">
          <Button
            variant="outline"
            onClick={handleBack}
            className="w-full sm:w-auto flex items-center justify-center gap-2 order-3 sm:order-1"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </Button>

          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto order-1 sm:order-2">
            {selectedPackage === 'free' && (
              <Button
                onClick={handleUpgrade}
                disabled={loading}
                className="w-full sm:w-auto bg-green-600 text-white hover:bg-green-700"
              >
                {loading ? 'Selecting...' : 'Select Free Package'}
              </Button>
            )}
            
            {(selectedPackage === 'free' || currentPackage === 'free') && !selectedPackage && (
              <Button
                onClick={() => navigate('/consultation')}
                className="w-full sm:w-auto bg-green-600 text-white hover:bg-green-700"
              >
                Schedule Free Consultation
              </Button>
            )}
            
            {selectedPackage && selectedPackage !== 'free' && (
              <Button
                onClick={handleUpgrade}
                disabled={loading}
                className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {loading ? 'Processing...' : 'Proceed to Payment'}
              </Button>
            )}
            
            <Button
              onClick={handleContinue}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <span>Continue</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default YourPackage;
